import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './NotificationSettings.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, BackButton } from './shared';
import { useLogout, useUserData } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function NotificationSettings() {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();

  const [preferences, setPreferences] = useState({
    notifications_enabled: true,
    notify_comment: true,
    notify_like: true,
    notify_heart: true,
    notify_chat: true,
    notify_status: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  // Fetch preferences on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/notification-preferences`);
        setPreferences(response.data);
      } catch (err) {
        console.error('Failed to fetch notification preferences:', err);
        // Use defaults on error
      } finally {
        setIsLoading(false);
      }
    };
    fetchPreferences();
  }, []);

  const handleToggle = async (key) => {
    const newValue = !preferences[key];
    const updatedPreferences = { ...preferences, [key]: newValue };

    // Optimistically update UI
    setPreferences(updatedPreferences);
    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await axios.put(`${API_BASE_URL}/api/notification-preferences`, {
        [key]: newValue
      });
      // Update with server response
      setPreferences(response.data);
    } catch (err) {
      console.error('Failed to update notification preference:', err);
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: !newValue }));
      setSaveError(t('notificationSettings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const renderToggle = (key, label, description = null, disabled = false) => {
    const isDisabled = disabled || isSaving || (!preferences.notifications_enabled && key !== 'notifications_enabled');

    return (
      <div className={`notification-toggle-row ${isDisabled ? 'disabled' : ''}`}>
        <button
          type="button"
          className={`toggle-button ${preferences[key] ? 'active' : ''}`}
          onClick={() => !isDisabled && handleToggle(key)}
          disabled={isDisabled}
          aria-pressed={preferences[key]}
          aria-label={label}
        >
          <span className="toggle-track">
            <span className="toggle-thumb" />
          </span>
        </button>
        <div className="toggle-label-container">
          <span className="toggle-label">{label}</span>
          {description && <span className="toggle-description">{description}</span>}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="notification-settings-page">
        <AppHeader
          userData={userData}
          onLogout={handleLogout}
          isLoggingOut={isLoggingOut}
          logoutError={logoutError}
          onClearLogoutError={clearLogoutError}
        />
        <div className="tab-navigation-notifications">
          <span className="tab-title">{t('notificationSettings.title')}</span>
        </div>
        <main className="notification-settings-content">
          <div className="loading-indicator">{t('loading')}</div>
        </main>
        <BottomNavigation activeTab="profile" onCreateClick={() => {}} />
      </div>
    );
  }

  return (
    <div className="notification-settings-page">
      {/* Header Navigation Bar - Using shared AppHeader component */}
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation-notifications">
        <span className="tab-title">{t('notificationSettings.title')}</span>
      </div>

      {/* Notification Settings Content */}
      <main className="notification-settings-content">
        {/* Back Button */}
        <BackButton
          onClick={navigation.onNavigateBack}
          className="back-button-content"
        />

        {/* Save Error */}
        {saveError && (
          <div className="save-error-message">
            {saveError}
          </div>
        )}

        {/* Master Toggle */}
        <div className="notification-section master-toggle">
          {renderToggle('notifications_enabled', t('notificationSettings.allNotifications'))}
        </div>

        {/* Individual Toggles */}
        <div className={`notification-section individual-toggles ${!preferences.notifications_enabled ? 'disabled-section' : ''}`}>
          {renderToggle('notify_comment', t('notificationSettings.newComment'))}
          {renderToggle('notify_like', t('notificationSettings.newLike'))}
          {renderToggle('notify_heart', t('notificationSettings.newHeart'))}
          {renderToggle('notify_chat', t('notificationSettings.newChat'))}
          {renderToggle(
            'notify_status',
            t('notificationSettings.newStatus'),
            t('notificationSettings.statusDescription')
          )}
        </div>

        {isSaving && (
          <div className="saving-indicator">
            {t('notificationSettings.saving')}
          </div>
        )}
      </main>

      {/* Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="profile"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Create Options Menu - Using shared component */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />
    </div>
  );
}

export default NotificationSettings;
